<script lang="ts">
  /* Landing page for a signed-in account that belongs to no company.

     Two ways to get here: an operator account created by `createsuperadmin`
     (no membership by design), or an invitee whose membership was removed.
     Before this page existed the route was pushed but never declared, so the
     router's "*" rendered the login form again — a successful login that
     looked like a failed one. */
  import { onMount } from "svelte";
  import { _ } from "svelte-i18n";
  import { push } from "svelte-spa-router";
  import LanguageSwitcher from "$components/LanguageSwitcher.svelte";
  import { auth, loadSession, logout } from "$lib/auth";

  let busy = $state(false);

  onMount(async () => {
    if (!$auth.authenticated && !$auth.loading) await loadSession();
    // A membership arrived (invitation accepted in another tab) — go home.
    if ($auth.activeTenantSlug) push(`/t/${$auth.activeTenantSlug}/dashboard`);
  });

  async function onLogout() {
    busy = true;
    try {
      await logout();
    } finally {
      busy = false;
      push("/login");
    }
  }
</script>

<div class="auth-page">
  <div class="auth-card">
    <div class="lang-row"><LanguageSwitcher /></div>

    <div class="brand">
      <span class="material-symbols-outlined brand-icon">domain_disabled</span>
      <h2>{$_("noTenant.title")}</h2>
      <p class="subtitle">{$_("noTenant.body")}</p>
    </div>

    {#if $auth.user?.email}
      <p class="who">{$auth.user.email}</p>
    {/if}

    <div class="actions">
      {#if $auth.user?.is_staff}
        <a class="btn--lg btn--primary" href="#/backoffice">{$_("shell.menu.backoffice")}</a>
      {:else}
        <!-- A forward path: before this the only button was Logout. -->
        <a class="btn--lg btn--primary" href="#/register">{$_("noTenant.createCompany")}</a>
      {/if}
      <button class="btn--lg btn--ghost" onclick={onLogout} disabled={busy}>
        {$_("shell.logout")}
      </button>
    </div>
  </div>
</div>

<style>
  .auth-page {
    min-height: 100vh;
    display: flex; align-items: center; justify-content: center;
    background: var(--color-bg); padding: 20px;
  }
  .auth-card {
    width: 100%; max-width: 440px; background: #fff; padding: 32px 40px 36px;
    border-radius: 12px; box-shadow: 0 10px 25px rgba(0,0,0,0.05);
    display: flex; flex-direction: column; gap: 20px;
  }
  .lang-row { display: flex; justify-content: flex-end; }
  .brand { text-align: center; }
  .brand-icon { font-size: 44px !important; color: var(--color-primary); }
  .brand h2 { margin: 10px 0 0; font-size: 23px; color: var(--color-surface-dark); }
  .subtitle { margin: 8px 0 0; color: var(--color-text-muted); font-size: 14px; line-height: 1.5; }
  .who {
    margin: 0; text-align: center; font-size: 13px; font-weight: 600;
    color: var(--color-surface-dark);
  }

  .actions { display: flex; flex-direction: column; gap: 10px; }
  .btn--lg {
    padding: 14px; font-size: 16px; font-weight: 600; border-radius: 6px;
    cursor: pointer; font-family: inherit; text-align: center; text-decoration: none;
  }
  .btn--primary { background: var(--color-primary); color: #fff; border: none; }
  .btn--ghost {
    background: #fff; color: var(--color-surface-dark);
    border: 1px solid var(--color-divider);
  }
  .btn--ghost:disabled { opacity: 0.6; cursor: default; }
</style>
